import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { Loader2, Save, Palette, Image as ImageIcon, UploadCloud, FileText, Package, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingKit, setDownloadingKit] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    companyName: '',
    address: '',
    accentColor: '#1e40af',
    footerText: '',
    logoS3Key: '',
  });

  const loadBranding = async () => {
    try {
      const r = await axios.get(`${API}/company/branding`);
      const b = r.data || {};
      setForm({
        companyName: b.companyName || '',
        address: b.address || '',
        accentColor: b.accentColor || '#1e40af',
        footerText: b.footerText || '',
        logoS3Key: b.logoS3Key || '',
      });
    } catch (err) {
      toast.error('Failed to load branding');
    }
  };

  useEffect(() => {
    loadBranding().finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/company/branding/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set('logoS3Key', res.data.s3Key);
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/company/branding`, form);
      // Re-fetch so the UI reflects exactly what the backend persisted.
      await loadBranding();
      toast.success('Report branding saved');
    } catch (err) {
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const downloadStoreKit = async () => {
    setDownloadingKit(true);
    try {
      const res = await axios.get(`${API}/store-kit/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'reasonvisionx-store-kit.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Store listing kit downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download store kit');
    } finally {
      setDownloadingKit(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  const logoUrl = form.logoS3Key ? `${API}/files/serve/${form.logoS3Key}` : null;

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('settings.title')}</h1>
          <p className="text-slate-600 mt-1">{t('settings.subtitle')}</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="branding-card">
          <div className="flex items-center gap-2 mb-1">
            <Palette className="w-5 h-5 text-indigo-500" />
            <h2 className="text-lg font-semibold text-slate-900">Report Branding</h2>
          </div>
          <p className="text-sm text-slate-600 mb-5">
            These details appear on the header and footer of all PDF reports (analysis, batch, project, analytics).
          </p>

          <div className="space-y-5">
            {/* Logo */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Company Logo</label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden" data-testid="branding-logo-preview">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-slate-300" />
                  )}
                </div>
                <div>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleLogo} data-testid="branding-logo-input" />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg disabled:opacity-50"
                    data-testid="branding-logo-upload-btn"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                    {uploading ? 'Uploading…' : 'Upload Logo'}
                  </button>
                  <p className="text-xs text-slate-400 mt-1">PNG, JPG or WEBP</p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Report Company Name</label>
              <input
                type="text"
                value={form.companyName}
                onChange={(e) => set('companyName', e.target.value)}
                placeholder="e.g. Acme Construction Ltd."
                className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="branding-company-name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
              <textarea
                value={form.address}
                onChange={(e) => set('address', e.target.value)}
                rows={2}
                placeholder="Head office address shown under the report title"
                className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="branding-address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Accent Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.accentColor || '#1e40af'}
                  onChange={(e) => set('accentColor', e.target.value)}
                  className="w-12 h-10 rounded border border-slate-300 cursor-pointer"
                  data-testid="branding-accent-color"
                />
                <input
                  type="text"
                  value={form.accentColor}
                  onChange={(e) => set('accentColor', e.target.value)}
                  placeholder="#1e40af"
                  className="w-40 px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg outline-none font-mono text-sm"
                  data-testid="branding-accent-hex"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                <FileText className="w-4 h-4 text-slate-400" /> Footer / Disclaimer
              </label>
              <textarea
                value={form.footerText}
                onChange={(e) => set('footerText', e.target.value)}
                rows={3}
                placeholder="Confidentiality or compliance disclaimer printed at the bottom of every report"
                className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="branding-footer"
              />
            </div>

            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 shadow-md"
              data-testid="branding-save-btn"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Save Branding
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="store-kit-card">
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-5 h-5 text-emerald-500" />
            <h2 className="text-lg font-semibold text-slate-900">Store Listing Kit</h2>
          </div>
          <p className="text-sm text-slate-600 mb-5">
            One-click, submission-ready archive bundling every localized marketing asset
            (English, Hindi, Tamil, Telugu, Bengali): app-store screenshots, listing copy,
            TTS voiceovers, the feature graphic and preview videos.
          </p>
          <button
            onClick={downloadStoreKit}
            disabled={downloadingKit}
            className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 shadow-md"
            data-testid="store-kit-download-btn"
          >
            {downloadingKit ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            {downloadingKit ? 'Preparing…' : 'Download Store Kit (.zip)'}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
